import { ApiRetailerPaymentType } from '@vend/types'
import { round, vn } from 'vend-number'
import uuid from '@vend/vend-uuid'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  canApplySurcharge,
  getTipEligibleAmount,
} from '../../../../functions/payment-types/lspay'
import { isBlindReturn, isReturn } from '../../../../functions/sale'
import {
  getAllPaymentsForPaymentType,
  getPaymentIds,
  hasPaymentsForOtherPaymentType,
  hasAnyProcessingServicePayment,
} from '../../../../functions/payments'
import { LSPayPaymentOption } from '../PaymentModal/types'
import { RecoveryPaymentOptions } from './useTransactionRecovery'

/**
 *
 * @param amountTendered
 * @returns Function to build LSPay payment options for the LSPay payment type modal
 */
export const useHandleLSPay = (amountTendered: string) => {
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const sale = useAppSelector(selectCurrentSale)

  const handleLSPay = (
    paymentType: ApiRetailerPaymentType,
    recoveryPaymentOptions?: RecoveryPaymentOptions
  ): LSPayPaymentOption => {
    const activeOutlet = registerManager.getActiveOutlet()
    const lspaySurchargeOn = getFeatureFromCache('lspay_surcharge')
    const hasQBO = getFeatureFromCache('has_qbo')
    const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
    const surchargeEnabledForXero = getFeatureFromCache(
      'lspay_surcharge_xero_onaccount'
    )
    const surchargeEnabledForQbo = getFeatureFromCache(
      'lspay_surcharge_qbo_onaccount'
    )
    const lspayTippingOnPretaxAmount = getFeatureFromCache(
      'lspay_tipping_on_pre_tax_amount'
    )

    const tipAllowed =
      lspayTippingOnPretaxAmount && !isReturn(sale) && !isBlindReturn(sale)

    const couldSurchargeBeApplied = canApplySurcharge(
      amountTendered,
      {
        ...sale,
        status: sale.status,
      },
      hasQBO,
      hasXeroEnabled,
      surchargeEnabledForXero,
      surchargeEnabledForQbo
    )
    const { intentId, transactionId, recoveryAmount } =
      recoveryPaymentOptions || {}

    const options: LSPayPaymentOption = {
      paymentModalType: 'lsPay',
      amount: vn(recoveryAmount || amountTendered).toNumber(),
      outletId: activeOutlet.id,
      currency: activeOutlet.currency,
      register: registerManager.getActiveRegister(),
      registerSaleId: sale.localId,
      paymentType,
      transactionId: transactionId || uuid(),
      paymentIntentId: intentId,
      isRecovery: !!transactionId,
      withSurcharge: lspaySurchargeOn && couldSurchargeBeApplied,
      tipEligibleAmount: tipAllowed
        ? vn(round(getTipEligibleAmount(amountTendered, sale), 2)).toNumber()
        : undefined,
      customerId: sale?.customer?.id,
    }

    if (isReturn(sale)) {
      const originalRegisterSalePayments = getAllPaymentsForPaymentType({
        payments: sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })

      options.originalRegisterSalePaymentIds = getPaymentIds(
        originalRegisterSalePayments
      )
      options.originalSalePaidByOtherPT = hasPaymentsForOtherPaymentType({
        payments: sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })
      // Calculate total amount from original LSPay payments for split-tender detection
      options.originalLspayPaymentAmount = originalRegisterSalePayments.reduce(
        (total, payment) => total + Number(payment.amount),
        0
      )
      // Check if any of the original payments were processed via Processing Service.
      // This flag ensures refunds use LSPAYRefundClientDriven component with the legacy API endpoint,
      // as the server-driven API endpoint doesn't support Processing Service refunds yet.
      options.originalSaleHasProcessingServicePayment = hasAnyProcessingServicePayment(
        originalRegisterSalePayments
      )
    }

    return options
  }

  return {
    handleLSPay,
  }
}
